import { Center } from '@mantine/core';
import useRetrieveDataset from '@/hooks/api/datasets/useRetrieveDataset.ts';
import CustomSpinner from '@/components/Spinner.tsx';
import DataViewer from '@/layouts/DataViewer/DataViewer.tsx';
import useGetDatasetUrlQuery from '@/hooks/api/datasets/useGetDatasetUrlQuery.ts';
import { lazy, Suspense, useEffect } from 'react';
import { isDirectory } from '@/utils/datasetTypes.ts';
import DatasetViewerHeader from '@/layouts/DataViewer/DataViewerHeader.tsx';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useViewDataset } from '@/hooks/useViewDataset.ts';
import LazyLoadErrorBoundary from '@/components/LazyLoadErrorBoundary.tsx';

const DirectoryViewer = lazy(() => import('@/layouts/DataViewer/Viewers/DirectoryViewer.tsx'));

type props = {
  datasetId?: string;
  setDatasetId: (id?: string) => void;
  isResultPage?: boolean;
};

const DatasetViewerContainer = ({ datasetId, setDatasetId, isResultPage = false }: props) => {
  const { data: dataset, error } = useRetrieveDataset(datasetId || undefined);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const {
    datasetId: queryDatasetId,
    setDatasetId: setQueryDatasetId,
    datasetModalId,
    setDatasetModalId,
  } = useViewDataset();

  useEffect(() => {
    if (!isResultPage && error?.response?.status === 404 && datasetId) {
      if (datasetId === queryDatasetId) setQueryDatasetId(undefined);
      if (datasetId === datasetModalId) setDatasetModalId(undefined);
    }
  }, [
    datasetId,
    navigate,
    error,
    searchParams,
    isResultPage,
    queryDatasetId,
    setQueryDatasetId,
    datasetModalId,
    setDatasetModalId,
  ]);

  const showAsDirectory = isDirectory(dataset);

  // if the dataset is a directory, don't fetch the URL automatically
  const { data: datasetUrlResponse } = useGetDatasetUrlQuery(datasetId, !showAsDirectory);

  if (!dataset || (!isDirectory(dataset) && !datasetUrlResponse?.url)) {
    return (
      <Center h={'100%'} pos={'absolute'} left={0} top={0} w={'100%'}>
        <CustomSpinner size={'md'} />
      </Center>
    );
  }

  if (isDirectory(dataset)) {
    return (
      <>
        <DatasetViewerHeader
          dataset={dataset}
          setDatasetId={setDatasetId}
          isResultPage={isResultPage}
        />
        <LazyLoadErrorBoundary resetKey={dataset.id}>
          <Suspense fallback={<CustomSpinner />}>
            <DirectoryViewer dataset={dataset} />
          </Suspense>
        </LazyLoadErrorBoundary>
      </>
    );
  }

  return (
    <>
      <DatasetViewerHeader
        dataset={dataset}
        setDatasetId={setDatasetId}
        isResultPage={isResultPage}
      />
      <DataViewer
        dataset={dataset}
        datasetURL={datasetUrlResponse!.url}
        fileName={datasetUrlResponse!.name}
        blob_exists={datasetUrlResponse!.blob_exists}
      />
    </>
  );
};

export default DatasetViewerContainer;
